import { List } from "immutable";
import { RootState } from "common/reducers";
import { estimate as estimateSelector } from "common/selectors/estimate.selectors";
import { byId as lineItemsById } from "common/selectors/estimateLineItem.selectors";
import { estimateGroup as estimateGroupSelector } from "common/selectors/estimateGroup.selectors";

/**
 * Finds the index of a visible (non-destroyed) item in the full line_item_ids list,
 * given its index in the visible-only list.
 */
export function visibleIndexToFullIndex(
  lineItemIds: List<number>,
  visibleIndex: number,
  lineItemsMap: ReturnType<typeof lineItemsById>,
): number {
  let visibleCount = 0;
  for (let i = 0; i < lineItemIds.size; i++) {
    const id = lineItemIds.get(i)!;
    if (lineItemsMap.get(id)?._destroy) continue;
    if (visibleCount === visibleIndex) return i;
    visibleCount++;
  }
  return lineItemIds.size;
}

/**
 * Renumbers an estimate's group sort_orders to stay globally sequential (mirrors rsf-app
 * Estimate.orderGroups). Included groups sort before excluded, then by existing sort_order;
 * `_destroy` groups are skipped and pushed to the end of `group_ids` so visible-tab indexes match
 * persisted sort_orders.
 *
 * @param {RootState} state The RootState
 * @param {number} estimateId The estimate whose groups to renumber
 * @returns {RootState} the updated state
 */
export const renumberEstimateGroupSortOrders = (state: RootState, estimateId: number): RootState => {
  const estimateRecord = estimateSelector(state, { estimateId });
  if (!estimateRecord) return state;

  const groupIds = estimateRecord.group_ids;
  if (groupIds.size === 0) return state;

  const all = groupIds.toArray().map((id) => {
    const g = estimateGroupSelector(state, { estimateGroupId: id });
    return { id, included: !!g?.included, sortOrder: g?.sort_order ?? 0, destroyed: !!g?._destroy };
  });

  const visible = all
    .filter((g) => !g.destroyed)
    .sort((a, b) => {
      if (a.included !== b.included) return a.included ? -1 : 1;
      return a.sortOrder - b.sortOrder;
    });
  const destroyed = all.filter((g) => g.destroyed);

  state = state.setIn(
    ["estimates", "byId", estimateId, "group_ids"],
    groupIds.clear().push(...visible.map((g) => g.id), ...destroyed.map((g) => g.id)),
  );
  visible.forEach(({ id }, idx) => {
    state = state.setIn(["estimateGroups", "byId", id, "sort_order"], idx);
  });
  return state;
};
